/**
 * Custom Reports API Service (KAN-87)
 * CRUD for custom report templates, source schema, filter options, and file generation.
 */

import { apiClient, APIResponse } from './client';
import { auth } from '../../config/firebase';
import type {
  AssetClass,
  CustomReportTemplate,
  CustomReportTemplateSummary,
  CustomReportGenerateRequest,
  TradesSourceSchema,
  FilterOptions,
  ClientUser,
} from '../../types/customReports';


class CustomReportService {

  // ========== User Template CRUD ==========

  async listUserTemplates(
    clientId: string,
    uid: string,
  ): Promise<CustomReportTemplateSummary[]> {
    const resp = await apiClient.get<CustomReportTemplateSummary[]>(
      `/api/v1/custom-reports/${clientId}/users/${uid}/templates`
    );
    return resp.data;
  }

  async getUserTemplate(
    clientId: string,
    uid: string,
    templateId: string,
  ): Promise<CustomReportTemplate> {
    const resp = await apiClient.get<CustomReportTemplate>(
      `/api/v1/custom-reports/${clientId}/users/${uid}/templates/${templateId}`
    );
    return resp.data;
  }

  async createUserTemplate(
    clientId: string,
    uid: string,
    template: Omit<CustomReportTemplate, 'templateId'>,
  ): Promise<CustomReportTemplate> {
    const resp = await apiClient.post<CustomReportTemplate>(
      `/api/v1/custom-reports/${clientId}/users/${uid}/templates`,
      template
    );
    return resp.data;
  }

  async updateUserTemplate(
    clientId: string,
    uid: string,
    templateId: string,
    template: Partial<CustomReportTemplate>,
  ): Promise<CustomReportTemplate> {
    const resp = await apiClient.put<CustomReportTemplate>(
      `/api/v1/custom-reports/${clientId}/users/${uid}/templates/${templateId}`,
      template
    );
    return resp.data;
  }

  async deleteUserTemplate(
    clientId: string,
    uid: string,
    templateId: string,
  ): Promise<void> {
    await apiClient.delete(
      `/api/v1/custom-reports/${clientId}/users/${uid}/templates/${templateId}`
    );
  }

  async duplicateUserTemplate(
    clientId: string,
    uid: string,
    templateId: string,
  ): Promise<CustomReportTemplate> {
    const resp = await apiClient.post<CustomReportTemplate>(
      `/api/v1/custom-reports/${clientId}/users/${uid}/templates/${templateId}/duplicate`
    );
    return resp.data;
  }

  // ========== Shared Template CRUD ==========

  async createSharedTemplate(
    clientId: string,
    template: Omit<CustomReportTemplate, 'templateId'>,
  ): Promise<CustomReportTemplate> {
    const resp = await apiClient.post<CustomReportTemplate>(
      `/api/v1/custom-reports/${clientId}/shared/templates`,
      template
    );
    return resp.data;
  }

  async listSharedTemplates(
    clientId: string,
  ): Promise<CustomReportTemplateSummary[]> {
    const resp = await apiClient.get<CustomReportTemplateSummary[]>(
      `/api/v1/custom-reports/${clientId}/shared/templates`
    );
    return resp.data;
  }

  async getSharedTemplate(
    clientId: string,
    templateId: string,
  ): Promise<CustomReportTemplate> {
    const resp = await apiClient.get<CustomReportTemplate>(
      `/api/v1/custom-reports/${clientId}/shared/templates/${templateId}`
    );
    return resp.data;
  }

  async updateSharedTemplate(
    clientId: string,
    templateId: string,
    template: Partial<CustomReportTemplate>,
  ): Promise<CustomReportTemplate> {
    const resp = await apiClient.put<CustomReportTemplate>(
      `/api/v1/custom-reports/${clientId}/shared/templates/${templateId}`,
      template
    );
    return resp.data;
  }

  async deactivateSharedTemplate(
    clientId: string,
    templateId: string,
  ): Promise<void> {
    await apiClient.delete(
      `/api/v1/custom-reports/${clientId}/shared/templates/${templateId}`
    );
  }

  async duplicateSharedTemplate(
    clientId: string,
    templateId: string,
  ): Promise<CustomReportTemplate> {
    const resp = await apiClient.post<CustomReportTemplate>(
      `/api/v1/custom-reports/${clientId}/shared/templates/${templateId}/duplicate`
    );
    return resp.data;
  }

  // ========== Sharing ==========

  async getClientUsers(clientId: string): Promise<ClientUser[]> {
    const resp = await apiClient.get<ClientUser[]>(
      `/api/v1/custom-reports/${clientId}/client-users`
    );
    return resp.data;
  }

  async shareTemplate(
    clientId: string,
    uid: string,
    templateId: string,
    sharedWith: string[],
  ): Promise<CustomReportTemplate> {
    const resp = await apiClient.put<CustomReportTemplate>(
      `/api/v1/custom-reports/${clientId}/users/${uid}/templates/${templateId}/share`,
      { sharedWith }
    );
    return resp.data;
  }

  async duplicateSharedWithMeTemplate(
    clientId: string,
    templateId: string,
    ownerUid: string,
  ): Promise<CustomReportTemplate> {
    const resp = await apiClient.post<CustomReportTemplate>(
      `/api/v1/custom-reports/${clientId}/shared-with-me/templates/${templateId}/duplicate`,
      { ownerUid }
    );
    return resp.data;
  }

  async listSharedWithMeTemplates(
    clientId: string,
  ): Promise<CustomReportTemplateSummary[]> {
    const resp = await apiClient.get<CustomReportTemplateSummary[]>(
      `/api/v1/custom-reports/${clientId}/shared-with-me/templates`
    );
    return resp.data;
  }

  // ========== Source Schema ==========

  async getSourceSchema(
    clientId: string,
    sourceType: 'TRADES' | 'EVENT_LEVEL_REPORTS' = 'TRADES',
    assetClass: AssetClass = 'FX',
  ): Promise<TradesSourceSchema> {
    const resp = await apiClient.get<TradesSourceSchema>(
      `/api/v1/custom-reports/${clientId}/source-schema/${sourceType}?assetClass=${assetClass}`
    );
    return resp.data;
  }

  // ========== Filter Options ==========

  async getFilterOptions(
    clientId: string,
    assetClass: AssetClass = 'FX',
  ): Promise<FilterOptions> {
    const resp = await apiClient.get<FilterOptions>(
      `/api/v1/custom-reports/${clientId}/filter-options?assetClass=${assetClass}`
    );
    return resp.data;
  }

  // ========== File Generation ==========

  async generate(
    clientId: string,
    request: CustomReportGenerateRequest,
  ): Promise<{ blob: Blob; filename: string }> {
    const baseUrl = import.meta.env.VITE_API_URL || 'http://127.0.0.1:8000';
    const token = await auth.currentUser?.getIdToken();

    const response = await fetch(
      `${baseUrl}/api/v1/custom-reports/${clientId}/generate`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(request),
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Failed to generate report' }));
      throw new Error(errorData.detail || errorData.message || 'Failed to generate report');
    }

    const disposition = response.headers.get('Content-Disposition');
    const match = disposition?.match(/filename="?([^"]+)"?/);
    const filename = match?.[1] || `custom_report_${new Date().toISOString().slice(0, 10)}.csv`;
    const blob = await response.blob();
    return { blob, filename };
  }
}

export const customReportService = new CustomReportService();
export default customReportService;
